import {
  type DocumentData,
  type Firestore,
  collection,
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
  updateDoc,
} from 'firebase/firestore';
import {
  CampaignSchema,
  CampaignCreateInputSchema,
  CampaignUpdateInputSchema,
  type Campaign,
  type CampaignCreateInput,
  type CampaignUpdateInput,
} from '@taki/data-model';
import { CURRENT_SCHEMA_VERSION, buildUpdateData, createConverter } from './standard-fields.js';
import { type CountOptions, type ListOptions, runCount, runList } from './list-query.js';
import { createArchiveOps } from './archive-ops.js';

/**
 * Repository de la famille `campaign` (action de communication groupée, org-wide en v0).
 *
 * Même patron que `asset` : l'entrée est VALIDÉE par Zod
 * (`CampaignCreateInputSchema`) AVANT toute écriture Firestore. Les champs
 * standard sont remplis par le SDK, les requêtes `list` sont cadrées par orgId.
 * Le public ciblé (audienceGroupIds vers `group`, audienceSnapshotContactIds
 * vers `contact`) et les autres liens sont de simples ids. Les familles aval
 * (message, opportunity, donation, consent) portent `campaignId` de leur côté.
 */

export type { CampaignCreateInput };

/** Nom de la collection Firestore de la famille campaign. */
export const CAMPAIGNS_COLLECTION = 'campaigns';

/**
 * Repository typé pour la famille `campaign`.
 * Toute lecture est validée par `CampaignSchema`, toute écriture par
 * `CampaignCreateInputSchema` (cage IA : aucun accès Firestore direct hors SDK).
 */
export interface CampaignRepository {
  create(input: CampaignCreateInput): Promise<Campaign>;
  list(options?: ListOptions): Promise<Campaign[]>;
  count(options?: CountOptions): Promise<number>;
  get(id: string): Promise<Campaign | null>;
  update(id: string, patch: CampaignUpdateInput): Promise<Campaign>;
  /** Archive (`archivedAt`). Semantique commune : cf. archive-ops.ts. */
  archive(id: string): Promise<Campaign>;
  /** Desarchive (`archivedAt` = null, JAMAIS deleteField). Cf. archive-ops.ts. */
  unarchive(id: string): Promise<Campaign>;
  /** Supprime (`deletedAt` + `archivedAt`), invisible cote client. Cf. archive-ops.ts. */
  remove(id: string): Promise<Campaign>;
}

// Relecture : le converter generique convertit deja TOUS les Timestamps
// (standard et metier) en Date, cf. standard-fields.ts.
const campaignConverter = createConverter<Campaign>((raw: DocumentData) => CampaignSchema.parse(raw));

/**
 * @param getActorId - retourne l'uid de l'utilisateur authentifié courant
 *   (injecté par `createTakiSdk`). Les Security Rules exigent
 *   createdBy/updatedBy == request.auth.uid.
 */
export function createCampaignRepository(
  db: Firestore,
  getActorId: () => string,
  orgId: string,
): CampaignRepository {
  const col = collection(db, CAMPAIGNS_COLLECTION).withConverter(campaignConverter);
  // Archiver, desarchiver, supprimer : la semantique des trois etats vit dans
  // `createArchiveOps` (archive-ops.ts), ecrite une seule fois pour les 55
  // familles. Une famille n'a plus rien a decider ici.
  const archiveOps = createArchiveOps<Campaign>(db, col, CAMPAIGNS_COLLECTION, getActorId);

  return {
    async create(input: CampaignCreateInput): Promise<Campaign> {
      // Valider AVANT d'écrire : une entrée invalide échoue ici, rien n'atteint
      // Firestore. (Même doctrine que le patron asset.)
      const fields = CampaignCreateInputSchema.parse(input);

      const actor = getActorId();
      const ref = doc(col);
      const now = new Date();

      // Champs métier optionnels : on ne les écrit que s'ils sont fournis
      // (évite d'ancrer des `undefined` parasites dans Firestore).
      const optional: DocumentData = {};
      if (fields.channel !== undefined) optional['channel'] = fields.channel;
      if (fields.audienceGroupIds !== undefined) optional['audienceGroupIds'] = fields.audienceGroupIds;
      if (fields.audienceSnapshotContactIds !== undefined) optional['audienceSnapshotContactIds'] = fields.audienceSnapshotContactIds;
      if (fields.scheduledAt !== undefined) optional['scheduledAt'] = fields.scheduledAt;
      if (fields.content !== undefined) optional['content'] = fields.content;
      if (fields.stats !== undefined) optional['stats'] = fields.stats;
      if (fields.utmRef !== undefined) optional['utmRef'] = fields.utmRef;
      if (fields.customFields !== undefined) optional['customFields'] = fields.customFields;

      const payload: DocumentData = {
        orgId: fields.orgId,
        type: fields.type,
        ...optional,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: actor,
        updatedBy: actor,
        archivedAt: null,
        deletedAt: null,
        schemaVersion: CURRENT_SCHEMA_VERSION,
      };

      await setDoc(ref, payload as unknown as Campaign);

      // setDoc ne résout pas serverTimestamp() : on valide une projection
      // cohérente avec ce qui vient d'être écrit (scheduledAt est déjà une Date
      // issue de l'entrée validée).
      return CampaignSchema.parse({
        id: ref.id,
        ...payload,
        createdAt: now,
        updatedAt: now,
      });
    },

    async list(options?: ListOptions): Promise<Campaign[]> {
      // Cadrage orgId, exclusion des archives, sous-type, tri, taille de page
      // et curseur : toute la semantique vit dans `runList` (list-query.ts),
      // ecrite une seule fois pour les 55 familles.
      return runList(col, orgId, options);
    },

    async count(options?: CountOptions): Promise<number> {
      // Compter sans charger : meme cadrage que `list`, sans page ni tri
      // (cf. runCount, list-query.ts). Un compteur calcule sur la page
      // courante ment des que la liste est paginee.
      return runCount(col, orgId, options);
    },

    async get(id: string): Promise<Campaign | null> {
      const ref = doc(col, id);
      const snap = await getDoc(ref);
      if (!snap.exists()) return null;
      return snap.data();
    },

    async update(id: string, patch: CampaignUpdateInput): Promise<Campaign> {
      // Valider AVANT d'ecrire (meme doctrine que create).
      const fields = CampaignUpdateInputSchema.parse(patch);
      const actor = getActorId();

      // Patch transforme pour updateDoc : customFields est eclate en chemins
      // pointes ('customFields.<cle>') pour fusionner cle par cle au lieu de
      // remplacer la map entiere (cf. buildUpdateData ; null = deleteField).
      const data = buildUpdateData(fields);
      data['updatedAt'] = serverTimestamp();
      data['updatedBy'] = actor;

      // Ref brute (sans converter) pour l'ecriture partielle : le converter
      // exigerait le document complet.
      await updateDoc(doc(db, CAMPAIGNS_COLLECTION, id), data);

      // Relire le document reel via la ref convertie : source de verite.
      const snap = await getDoc(doc(col, id));
      if (!snap.exists()) {
        throw new Error(`campaign ${id} introuvable apres mise a jour.`);
      }
      return snap.data();
    },

    // archive / unarchive / remove : cf. `archiveOps` plus haut.
    ...archiveOps,
  };
}
